const SWITCHER_SPINNER = `<div class="tab-grid-loading">
  <svg aria-hidden="true" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
  </svg>
</div>`;

const debounceCustomFacets = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

class CollectionSwitcherFacets extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.tabContents = this.querySelectorAll('[data-tab-content]');
    this.tabTitles = this.querySelectorAll('[data-tab-title]');
    this.prefetchStarted = false;

    this.tabStates = new Map();
    this.tabContents.forEach((tab) => {
      const tabId = tab.dataset.tabContent;
      this.tabStates.set(tabId, { sortBy: '', page: 1, filters: {}, filtersLoaded: false, cache: null });
      this.setupTabHandlers(tab);
    });

    this.tabTitles.forEach((btn) => {
      btn.addEventListener('click', this.onTabClick.bind(this), { capture: true });
    });

    this.initActiveTab();
  }

  initActiveTab() {
    const urlParam = new URLSearchParams(location.search).get(`tab_section_${this.sectionId}`);

    if (urlParam) {
      const targetTab = [...this.tabContents].find(
        (t) => t.dataset.collectionHandle === urlParam
      );
      if (targetTab) {
        const isAlreadyVisible = !targetTab.classList.contains('hidden');
        if (isAlreadyVisible) {
          this.fetchProducts(targetTab);
        } else {
          const tabId = targetTab.dataset.tabContent;
          const tabBtn = [...this.tabTitles].find((b) => b.dataset.tabTitle === tabId);
          if (tabBtn) tabBtn.click();
        }
        return;
      }
    }

    const firstTab = [...this.tabContents].find((t) => !t.classList.contains('hidden'));
    if (firstTab) this.fetchProducts(firstTab);
  }

  onTabClick(event) {
    const btn = event.currentTarget;
    if (btn.classList.contains('is-active')) return;

    const tabId = btn.dataset.tabTitle;
    const newTab = this.querySelector(`[data-tab-content="${tabId}"]`);
    if (!newTab) return;

    const state = this.tabStates.get(tabId);
    if (state) {
      state.page = 1;
      state.filters = {};
      const filterForm = document.querySelector(`[data-filter-form][data-tab-id="${tabId}"]`);
      if (filterForm) filterForm.reset();
    }

    const url = new URL(location.href);
    url.searchParams.set(`tab_section_${this.sectionId}`, newTab.dataset.collectionHandle);
    history.replaceState({}, '', url);

    this.fetchProducts(newTab);
  }

  setupTabHandlers(tabEl) {
    const tabId = tabEl.dataset.tabContent;

    const sortSelect = tabEl.querySelector('[data-sort-select]');
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        const state = this.tabStates.get(tabId);
        state.sortBy = e.target.value;
        state.page = 1;
        this.fetchProducts(tabEl);
      });
    }

    const clearBtn = document.querySelector(`[data-clear-filters][data-tab-id="${tabId}"]`);
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        const state = this.tabStates.get(tabId);
        state.filters = {};
        state.page = 1;
        const filterForm = document.querySelector(`[data-filter-form][data-tab-id="${tabId}"]`);
        if (filterForm) filterForm.reset();
        this.fetchProducts(tabEl);
      });
    }

    const applyBtn = document.querySelector(`[data-apply-filters="${tabId}"]`);
    if (applyBtn) {
      applyBtn.addEventListener('click', () => {
        const filterForm = document.querySelector(`[data-filter-form][data-tab-id="${tabId}"]`);
        if (filterForm) {
          const state = this.tabStates.get(tabId);
          state.filters = this.buildFilterState(filterForm);
          state.page = 1;
          this.fetchProducts(tabEl);
        }
      });
    }

    tabEl.addEventListener('click', (e) => {
      const link = e.target.closest('[data-pagination] a');
      if (!link) return;
      e.preventDefault();
      const pageMatch = link.getAttribute('href')?.match(/[?&]page=(\d+)/);
      const state = this.tabStates.get(tabId);
      state.page = pageMatch ? parseInt(pageMatch[1], 10) : 1;
      this.fetchProducts(tabEl);
    });

    tabEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pagination] infinite-scroll button');
      if (!btn || tabEl.classList.contains('processing')) return;
      e.preventDefault();
      const state = this.tabStates.get(tabId);
      state.page += 1;
      this.fetchProducts(tabEl, { append: true, loadingBtn: btn });
    });

    tabEl.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove-single-filter]');
      if (!removeBtn) return;
      e.preventDefault();
      e.stopPropagation();

      const paramName = removeBtn.dataset.filterParamName;
      const filterValue = removeBtn.dataset.filterValue;
      const state = this.tabStates.get(tabId);
      const filterForm = document.querySelector(`[data-filter-form][data-tab-id="${tabId}"]`);

      if (filterValue !== undefined) {
        if (state.filters[paramName]) {
          const remaining = state.filters[paramName].split(',').filter((v) => v !== filterValue);
          if (remaining.length === 0) {
            delete state.filters[paramName];
          } else {
            state.filters[paramName] = remaining.join(',');
          }
        }
        if (filterForm) {
          const cb = filterForm.querySelector(`input[name="${paramName}"][value="${filterValue}"]`);
          if (cb) cb.checked = false;
        }
      } else {
        // Price range — remove both .gte and .lte, reset slider silently
        delete state.filters[`${paramName}.gte`];
        delete state.filters[`${paramName}.lte`];
        if (filterForm) {
          const slider = filterForm.querySelector('price-range-slider');
          if (slider) slider.reset();
        }
      }

      state.page = 1;
      this.fetchProducts(tabEl);
    }, { capture: true });
  }

  initTabFilters(tabEl, doc) {
    const tabId = tabEl.dataset.tabContent;
    const filterForm = document.querySelector(`[data-filter-form][data-tab-id="${tabId}"]`);
    if (!filterForm) return;

    const fetchedFilterForm = doc.querySelector('.filter-form');
    if (!fetchedFilterForm) return;

    filterForm.innerHTML = fetchedFilterForm.innerHTML;

    filterForm.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.addEventListener(
        'change',
        debounceCustomFacets(() => {
          const state = this.tabStates.get(tabId);
          state.filters = this.buildFilterState(filterForm);
          state.page = 1;
          this.fetchProducts(tabEl);
        }, 150)
      );
    });

    filterForm.querySelectorAll('input[type="number"]').forEach((input) => {
      input.addEventListener(
        'change',
        debounceCustomFacets(() => {
          const state = this.tabStates.get(tabId);
          state.filters = this.buildFilterState(filterForm);
          state.page = 1;
          this.fetchProducts(tabEl);
        }, 300)
      );
    });
  }

  buildFilterState(form) {
    const filters = {};
    const checkboxGroups = {};

    form.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
      const name = cb.getAttribute('name');
      if (!checkboxGroups[name]) checkboxGroups[name] = [];
      checkboxGroups[name].push(cb.value);
    });

    for (const [name, values] of Object.entries(checkboxGroups)) {
      filters[name] = values.join(',');
    }

    form.querySelectorAll('input[type="number"]').forEach((input) => {
      const name = input.getAttribute('name');
      const val = input.value;
      const min = input.getAttribute('min');
      const max = input.getAttribute('max');
      if (!name || val === '' || (name.endsWith('.gte') && val === min) || (name.endsWith('.lte') && val === max)) return;
      filters[name] = val;
    });

    return filters;
  }

  isDefaultState(state) {
    return !state.sortBy && state.page === 1 && Object.keys(state.filters).length === 0;
  }

  applyDoc(tabEl, doc, { append = false } = {}) {
    const tabId = tabEl.dataset.tabContent;
    const state = this.tabStates.get(tabId);

    if (!state.filtersLoaded) {
      this.initTabFilters(tabEl, doc);
      state.filtersLoaded = true;
    }

    const productGridEl = tabEl.querySelector('[data-product-grid]');
    const paginationEl = tabEl.querySelector('[data-pagination]');
    const countEl = tabEl.querySelector('[data-product-count]');
    const applyCountEl = document.querySelector(`[data-apply-count="${tabId}"]`);
    const noResultsMsg = document.getElementById(`facets-no-results-message-${tabId}`);
    const fetchedGridEl = doc.querySelector('#product-grid-ajax');

    const countHtml = doc.querySelector('#products-count-ajax')?.innerHTML ?? '';

    if (productGridEl && fetchedGridEl) {
      if (append) {
        const temp = document.createElement('div');
        temp.innerHTML = fetchedGridEl.innerHTML;
        const newItems = Array.from(temp.children);
        newItems.forEach((child) => productGridEl.appendChild(child));
        this.animateGridItems(newItems);
      } else {
        productGridEl.innerHTML = fetchedGridEl.innerHTML;
        this.animateGridItems(Array.from(productGridEl.children));
      }
    }

    if (paginationEl) {
      paginationEl.innerHTML = doc.querySelector('#pagination-ajax')?.innerHTML ?? '';
      this.initInfiniteScroll(tabEl, paginationEl);
    }
    if (!append) {
      if (countEl) countEl.innerHTML = countHtml;
      if (applyCountEl) applyCountEl.innerHTML = countHtml;
    }

    const currentFacetsEl = tabEl.querySelector('[data-current-facets]');
    if (currentFacetsEl && !append) {
      currentFacetsEl.innerHTML = doc.querySelector('#current-facets-ajax')?.innerHTML ?? '';
    }

    if (noResultsMsg && !append) {
      const filteredCount = parseInt(fetchedGridEl?.dataset?.filteredCount ?? '1', 10);
      noResultsMsg.classList.toggle('hide', filteredCount !== 0);
    }
  }

  initInfiniteScroll(tabEl, paginationEl) {
    const infiniteEl = paginationEl.querySelector('infinite-scroll[data-trigger-type="infinite"]');
    if (!infiniteEl) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        if (tabEl.classList.contains('processing')) return;
        const state = this.tabStates.get(tabEl.dataset.tabContent);
        state.page += 1;
        const loadingBtn = infiniteEl.querySelector('button');
        this.fetchProducts(tabEl, { append: true, loadingBtn });
      });
    }, { threshold: 0.5 });

    observer.observe(infiniteEl);
  }

  async fetchProducts(tabEl, { append = false, loadingBtn = null } = {}) {
    if (!tabEl.dataset.collectionUrl) return;
    const tabId = tabEl.dataset.tabContent;
    const state = this.tabStates.get(tabId);

    // Use prefetched cache when on default state (no filters/sort/page applied)
    if (!append && this.isDefaultState(state) && state.cache) {
      this.applyDoc(tabEl, state.cache);
      return;
    }

    tabEl.classList.add('processing');
    if (loadingBtn) loadingBtn.classList.add('loading');

    const productGridEl = tabEl.querySelector('[data-product-grid]');
    if (productGridEl && !state.filtersLoaded && !append) productGridEl.innerHTML = SWITCHER_SPINNER;

    const params = new URLSearchParams();
    if (state.sortBy) params.set('sort_by', state.sortBy);
    if (state.page > 1) params.set('page', String(state.page));
    if (tabEl.dataset.view) params.set('view', tabEl.dataset.view);
    for (const [k, v] of Object.entries(state.filters)) params.set(k, v);

    try {
      const res = await fetch(`${tabEl.dataset.collectionUrl}?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      // Cache the default-state response for instant re-use
      if (!append && this.isDefaultState(state)) state.cache = doc;

      this.applyDoc(tabEl, doc, { append });

      // After the first active tab finishes, prefetch all other tabs
      if (!this.prefetchStarted && !append) {
        this.prefetchStarted = true;
        this.prefetchOtherTabs(tabEl);
      }
    } catch (err) {
      console.error('[CollectionSwitcherFacets] fetch failed:', err);
    } finally {
      tabEl.classList.remove('processing');
      if (loadingBtn) loadingBtn.classList.remove('loading');
    }
  }

  prefetchOtherTabs(activeTabEl) {
    const others = [...this.tabContents].filter((t) => t !== activeTabEl);
    const schedule = 'requestIdleCallback' in window ? requestIdleCallback : (cb) => setTimeout(cb, 200);

    others.forEach((tab) => {
      schedule(() => this.prefetchTab(tab));
    });
  }

  animateGridItems(items) {
    items.forEach((item, i) => {
      item.style.animationDelay = `${i * 40}ms`;
      item.classList.add('switcher-card-anim');
    });
  }

  async prefetchTab(tabEl) {
    if (!tabEl.dataset.collectionUrl) return;
    const tabId = tabEl.dataset.tabContent;
    const state = this.tabStates.get(tabId);
    if (state.cache) return; // already cached or fetched

    const params = new URLSearchParams();
    if (tabEl.dataset.view) params.set('view', tabEl.dataset.view);

    try {
      const res = await fetch(`${tabEl.dataset.collectionUrl}?${params.toString()}`);
      if (!res.ok) return;
      state.cache = new DOMParser().parseFromString(await res.text(), 'text/html');
    } catch {
      // silent — prefetch is best-effort
    }
  }
}

if (customElements.get('collection-switcher-facets') === undefined) {
  customElements.define('collection-switcher-facets', CollectionSwitcherFacets);
}
